import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import SortBar from "@/components/feature/SortBar";
import Pagination from "@/components/feature/Pagination";
import { useSeo } from "@/hooks/useSeo";
import { genreOptions, movies, regionOptions } from "@/mocks/movies";
import { emptyFilters, filterMovies, sortMovies, toggleInArray, type FilterState, type SortKey } from "@/utils/movieQuery";

const PAGE_SIZE = 12;

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get("q") ?? "";

  const [input, setInput] = useState(q);
  const [filters, setFilters] = useState<FilterState>(() => ({ ...emptyFilters, keyword: q }));
  const [sort, setSort] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);

  const categoryLabel = q ? `搜索“${q}”` : "搜索影视";

  useSeo(
    `${categoryLabel} - 看片网站`,
    `看片网站影视搜索结果：${q || "输入关键词搜索电影、电视剧"}，支持按类型、地区筛选与排序。`,
    `${q},影视搜索,在线电影,电视剧,看片网站`
  );

  useEffect(() => {
    setInput(q);
    setFilters({ ...emptyFilters, keyword: q });
    setPage(1);
  }, [q]);

  const submitted = q.trim().length > 0;

  const processed = useMemo(() => {
    if (!submitted) return [];
    return sortMovies(filterMovies(movies, filters), sort);
  }, [filters, sort, submitted]);

  const totalPages = Math.max(1, Math.ceil(processed.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = processed.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hotMovies = useMemo(() => [...movies].sort((a, b) => b.hot - a.hot).slice(0, 6), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line leading-none"></i>
        <span className="text-foreground-900">搜索</span>
      </nav>

      <div className="mt-4 border-b border-background-200/70 pb-5">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">影视搜索</h1>
        <p className="mt-2 text-sm text-foreground-600">输入影片或剧集名称，快速找到想看的内容</p>
      </div>

      <form onSubmit={submit} className="mt-5 flex flex-col gap-2 sm:flex-row">
        <div className="relative w-full">
          <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="text"
            placeholder="输入影视名称，如：星际、深海、长安"
            className="w-full rounded-md border border-background-300/70 bg-background-50 py-3 pl-10 pr-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {submitted && (
        <div className="mt-6 space-y-4">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
            <div className="mb-3 flex flex-wrap gap-2">
              <span className="text-xs font-bold text-foreground-700">题材：</span>
              {genreOptions.slice(0, 12).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => updateFilters({ ...filters, genres: toggleInArray(filters.genres, g) })}
                  className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                    filters.genres.includes(g)
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-200/70 text-foreground-600 hover:border-primary-300"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs font-bold text-foreground-700">地区：</span>
              {regionOptions.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => updateFilters({ ...filters, regions: toggleInArray(filters.regions, r) })}
                  className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                    filters.regions.includes(r)
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-200/70 text-foreground-600 hover:border-primary-300"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <SortBar sort={sort} onChange={(s) => { setSort(s); setPage(1); }} total={processed.length} />

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14 px-4 text-center">
              <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
                没有找到与“{q}”相关的作品
              </p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词，或减少筛选条件试试</p>
              <button
                type="button"
                onClick={() => updateFilters({ ...emptyFilters, keyword: q, genres: [] })}
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line leading-none"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      )}

      <section className="mt-10">
        <h2 className="font-heading text-lg font-bold text-foreground-950">
          <Link to="/category">大家都在看</Link>
        </h2>
        <p className="mt-1 text-xs text-foreground-500">热门推荐，或许有你感兴趣的作品</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
          {hotMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>
    </div>
  );
}